import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import values from 'lodash/values';
import Color from 'color';
import { Motion } from 'react-motion';
import { textColor } from '../../lib/Color';
import { springify } from '../../lib/Motion';
import { getPlanPrice, getPlanTitle } from '../../actions/plan';
import { EMPTY_OBJECT, RESPONSIVE_SIZE, PLAN } from '../../constants';
import Hoverable, { withHover } from '../Hoverable';
import { Info } from '../Icons';
import Button from '../Button';
import Style from '../Style';
import { H4, L1, L3, P1, P2 } from '../Typography';

class SubscriptionPlans extends React.PureComponent {
  static propTypes = {
    responsive: PropTypes.number,
    plan: PropTypes.object,
    onSelectPlan: PropTypes.func,
    showUnlimited: PropTypes.bool,
    bannerColor: PropTypes.string,
  };
  static defaultProps = {
    plan: EMPTY_OBJECT,
    onSelectPlan: name => {},
    showUnlimited: true,
  };

  constructor(props) {
    super(props);

    this.state = {
      tooltipVisible: false,
      tooltipPosition: [0, 0],
      tooltipTitle: '',
      tooltipText: '',
    };
  }

  toggleTooltip = (show, el, title, text) => {
    const triggerRect = el.getBoundingClientRect();
    const thisRect = this.tooltipContainer.getBoundingClientRect();

    const triggerCenter =
      (triggerRect.right - triggerRect.left) / 2 + triggerRect.left;
    const xOffset = triggerCenter - thisRect.left;
    const yOffset = thisRect.height - (triggerRect.top - thisRect.top);

    this.setState({
      tooltipVisible: show,
      tooltipTitle: title,
      tooltipText: text,
      tooltipPosition: [xOffset, yOffset],
    });
  };

  render() {
    const {
      responsive,
      plan,
      onSelectPlan,
      showUnlimited,
      bannerColor,
      ...otherProps
    } = this.props;
    const planName = plan.name;
    const annual = plan.annual;

    const planStyle = {
      margin: '1rem',
      flex: '1 1 18rem',
      width: '18rem',
      height: '25rem',
    };
    let buttonSize = 'small';

    return (
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          maxWidth: '75rem',
          margin: '-1rem auto',
        }}
        ref={el => (this.tooltipContainer = el)}
      >
        <div
          style={{
            pointerEvents: 'none',
            opacity: this.state.tooltipVisible ? 1 : 0,
            position: 'absolute',
            zIndex: 9,
            left: this.state.tooltipPosition[0],
            bottom: this.state.tooltipPosition[1],
          }}
        >
          <div
            style={{
              position: 'relative',
              bottom: '2rem',
              left: '-50%',
              width: '100vw',
              maxWidth: '21rem',
              boxShadow: `0 0.2rem 2.2rem 0 ${Color('#E4E6E8')
                .alpha(0.8)
                .string()}`,
            }}
          >
            <div
              style={{
                position: 'absolute',
                zIndex: 0,
                bottom: '-1rem',
                transformOrigin: '50%',
                transform: 'rotate(45deg)',
                left: 'calc(50% - 1.5rem)',
                width: '3rem',
                height: '3rem',
                backgroundColor: Style.Colors.white,
                boxShadow: `0 0.2rem 2.2rem 0 ${Color('#E4E6E8')
                  .alpha(0.8)
                  .string()}`,
              }}
            />
            <div
              style={{
                borderRadius: '0.6rem',
                position: 'relative',
                zIndex: 1,
                backgroundColor: Style.Colors.white,
                padding: '4rem 2rem 5rem',
              }}
            >
              <L3 style={{ color: Style.Colors.gray }}>
                {this.state.tooltipTitle}
              </L3>
              <P2 style={{ color: '#454E57', marginTop: '1rem' }}>
                {this.state.tooltipText}
              </P2>
            </div>
          </div>
        </div>

        <Hoverable style={{ ...planStyle }}>
          <Plan
            title={getPlanTitle(PLAN.BASIC)}
            price={getPlanPrice(PLAN.BASIC, annual)}
            benefits={[
              { text: '5 clips per month' },
              { text: '1080p HD resolution' },
            ]}
            buttonSize={buttonSize}
            onButtonClick={() => onSelectPlan(PLAN.BASIC)}
            selected={planName === PLAN.BASIC}
            onTooltipHover={this.toggleTooltip}
          />
        </Hoverable>

        <Hoverable style={{ ...planStyle }}>
          <Plan
            title={getPlanTitle(PLAN.PRO)}
            price={getPlanPrice(PLAN.PRO, annual)}
            benefits={[
              { text: '20 clips per month' },
              { text: '1080p HD resolution' },
            ]}
            buttonSize={buttonSize}
            onButtonClick={() => onSelectPlan(PLAN.PRO)}
            selected={planName === PLAN.PRO}
            onTooltipHover={this.toggleTooltip}
          />
        </Hoverable>

        {showUnlimited && (
          <Hoverable
            style={{
              ...planStyle,
              flex: '5 5 18rem',
              width: '18rem',
              height: '30rem',
            }}
          >
            <Plan
              title={getPlanTitle(PLAN.UNLIMITED)}
              price={getPlanPrice(PLAN.UNLIMITED, annual)}
              benefits={[
                { text: 'No download limit' },
                { text: '4K UHD resolution' },
                { text: '1080p HD resolution' },
                { text: 'After Effects Templates (Bonus)' },
              ]}
              buttonSize={buttonSize}
              onButtonClick={() => onSelectPlan(PLAN.UNLIMITED)}
              selected={planName === PLAN.UNLIMITED}
              bannerColor={bannerColor}
              bannerText={'BEST VALUE!'}
              onTooltipHover={this.toggleTooltip}
            />
          </Hoverable>
        )}
      </div>
    );
  }
}

function mapStateToProps(state, ownProps) {
  return {
    responsive: state.runtime.responsive,
    bannerColor: ownProps.bannerColor || state.ui.colors.dark,
  };
}

export default connect(mapStateToProps)(SubscriptionPlans);

class Plan extends React.PureComponent {
  static propTypes = {
    hover: PropTypes.bool,
    title: PropTypes.string,
    price: PropTypes.number,
    benefits: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        tooltip: PropTypes.shape({
          title: PropTypes.string,
          text: PropTypes.string,
        }),
      }),
    ),
    buttonSize: PropTypes.string,
    onButtonClick: PropTypes.func,
    selected: PropTypes.bool,
    bannerColor: PropTypes.string,
    bannerText: PropTypes.string,
    onTooltipHover: PropTypes.func,
  };
  static defaultProps = {};

  constructor(props) {
    super(props);

    this.state = {
      price: props.price,
    };

    this.tooltipRefs = {};
  }

  render() {
    const {
      hover,
      title,
      price,
      benefits,
      buttonSize,
      onButtonClick,
      selected,
      bannerColor,
      bannerText,
      onTooltipHover,
      ...otherProps
    } = this.props;

    const boxStyle = {
      backgroundColor: '#F9F9FA',
    };

    const animatedDefault = {
      hover: 0,
      selected: 0,
      price: this.state.price,
    };
    const animatedCurrent = {
      hover: hover ? 1 : 0,
      selected: selected ? 1 : 0,
      price: price,
    };

    const textColorBase = Color(Style.Colors.shadow3);
    const textColorHover = Color(Style.Colors.black);

    const borderColorBase = Color('#979797');
    const borderColorHover = Color('#4A4A4A');
    const borderColorSelected = Color(Style.Colors.black);

    return (
      <Motion defaultStyle={animatedDefault} style={springify(animatedCurrent)}>
        {tweens => {
          const borderColor = borderColorBase
            .mix(borderColorSelected, tweens.selected)
            .mix(borderColorHover, tweens.hover);

          const outerShadow = `${0.5 * tweens.hover}rem ${0.6 *
            tweens.hover}rem ${1.6 * tweens.hover}rem ${0 *
            tweens.hover}rem rgba(0,0,0,0.09)`;
          const innerShaddow = `0 0 0 ${0.1 *
            tweens.selected}rem ${borderColor}`;

          const benefitColor = textColorBase.mix(textColorHover, tweens.hover);
          const buttonColor = benefitColor.mix(
            Color(Style.Colors.black),
            tweens.selected,
          );

          return (
            <div
              style={{
                position: 'relative',
                height: '100%',
                textAlign: 'center',
                boxShadow: `${outerShadow}, ${innerShaddow}`,
                border: `0.1rem solid ${borderColor}`,
                borderRadius: '0.5rem',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                padding: '4rem 1rem 2.5rem',
                backgroundColor: Color(Style.Colors.shadow5).alpha(0.3),
                overflow: 'hidden',
              }}
              {...otherProps}
            >
              <div>
                <H4 style={{ fontSize: '3.2rem', marginBottom: 0 }}>{title}</H4>
                <P1 style={{ fontSize: '2.1rem', marginTop: 0 }}>
                  ${Math.round(tweens.price)}/mo.
                </P1>
                <div
                  style={{
                    display: bannerText && bannerColor ? 'flex' : 'none',
                    position: 'absolute',
                    right: 0,
                    top: '22%',
                    width: '100%',
                    transformOrigin: '100% 100%',
                    transform: 'rotate(43deg)',
                    height: '3rem',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    backgroundColor: bannerColor,
                    boxShadow: `0 0.2rem 0.3rem 0 ${Color('#9B9B9B')
                      .alpha(0.4)
                      .string()}, inset 0 -0.1rem 0.8rem 0 ${Color(
                      Style.Colors.black,
                    )
                      .alpha(0.2)
                      .string()}`,
                  }}
                >
                  <L1
                    style={{
                      fontWeight: 500,
                      marginRight: '3.2rem',
                      letterSpacing: '0.3px',
                      fontSize: '1.4rem',
                      color: Style.Colors.white,
                    }}
                  >
                    {bannerText}
                  </L1>
                </div>
              </div>

              <div style={{ color: benefitColor.string() }}>
                {benefits.map(benefit => (
                  <div key={benefit.text}>
                    <span>
                      <P2 style={{ display: 'inline-block', fontWeight: 400 }}>
                        {benefit.text}
                        {!!benefit.tooltip && (
                          <Hoverable
                            wrapWith="span"
                            onChange={hovering =>
                              onTooltipHover(
                                hovering,
                                this.tooltipRefs[benefit.text],
                                benefit.tooltip.title,
                                benefit.tooltip.text,
                              )}
                          >
                            <span
                              ref={el => (this.tooltipRefs[benefit.text] = el)}
                            >
                              <Info
                                color={benefitColor.string()}
                                style={{
                                  margin: '0 0.5rem',
                                  maxWidth: '1.4rem',
                                  position: 'relative',
                                  top: '0.2rem',
                                }}
                              />
                            </span>
                          </Hoverable>
                        )}
                      </P2>
                    </span>
                  </div>
                ))}
              </div>

              <Button
                hover={selected || undefined}
                darkColor={buttonColor.string()}
                size={buttonSize}
                checked={selected || undefined}
                onClick={onButtonClick}
                style={{ fontSize: '1.4rem' }}
              >
                Choose&nbsp;this&nbsp;plan
              </Button>
            </div>
          );
        }}
      </Motion>
    );
  }
}

const PlanHoverable = withHover()(Plan);



// WEBPACK FOOTER //
// ./src/components/SubscriptionPlans/SubscriptionPlans.js